import { t } from "i18next";
import {
  ZAttrEditInfo,
  cvtItemProps,
  editorsByType,
  getEditorInfo,
  zAttrComponentEditor,
} from "src/common/attrEdit";
import {
  makeFinancialInputProps,
  makeFormulaProps,
  makeFormulaWithFinancialProps,
  makeInputNumberProps,
  makeInputProps,
  makeObjectRefSelectorProps,
  makeObjectRefTableProps,
  makeSelectProps,
  makeTextAreaProps,
  makeUploaderProps,
} from "src/common/attrEdit/components";
import { makeImageCarouselProps } from "src/common/attrEdit/components/ZAttrImageCarousel";
import { makeMaskedInputProps } from "src/common/attrEdit/components/ZAttrMaskedInput";
import { makeAttrPersonSelectProps } from "src/common/attrEdit/components/ZAttrPersonSelect";
import { makeChildEntitiesProps } from "src/common/attrEdit/components/ZChildEntities";
import { getViewInfo } from "src/common/attrView";
import { makeAttrLinkedProps } from "src/common/attrView/componentsDefs";
import { AttrLinked } from "src/components/AttrLinked/AttrLinked";
import { ChildEntities } from "src/components/ChildEntities";
import { FinancialInput } from "src/components/FinancialInput/FinancialInput";
import {
  FormBlockItem,
  ItemProps,
  blockItem,
} from "src/components/FormWithBlocks";
import { readOnlyInput } from "src/components/FormWithBlocks/items";
import { ObjectRefSelector } from "src/components/ObjectRefSelector/ObjectRefSelector";
import { ObjectRefTable } from "src/components/ObjectRefTable/ObjectRefTable";
import { PersonSelect } from "src/components/PersonSelect";
import { AttrTypeName } from "src/types/AttrType";
import { Permission, hasPermissionIn } from "src/types/Permission";
import { ZAttribute } from "src/types/ZAttribute";
import { makeAttrColorCardProps } from "src/common/attrEdit/components/ZAttrColorCard";
import { ColorCard } from "src/components/ColorCard";
import { EntityCheckbock } from "../fieldComponents/EntityCheckbox";
import { EntityContentLanguage } from "../fieldComponents/EntityContentLanguage";
import { EntityDatePicker } from "../fieldComponents/EntityDatePicker";
import { EntityInput } from "../fieldComponents/EntityInput";
import {
  EntityInputNumber,
  PropsEntityInputNumber,
} from "../fieldComponents/EntityInputNumber";
import { EntityLinkEdit } from "../fieldComponents/EntityLinkEdit";
import { EntityMaskedInput } from "../fieldComponents/EntityMaskedInput";
import { EntitySwitch } from "../fieldComponents/EntitySwitch";
import { EntityTextArea } from "../fieldComponents/EntityTextArea";
import { EntityTimePicker } from "../fieldComponents/EntityTimePicker";
import { BuilderCtx, canUpdateAttr } from "./BuilderCtx";
import { buildImageCarousel } from "./buildImageCarousel";
import { buildReferenceItem } from "./buildReferenceItem";
import { buildUploader } from "./buildUploader";
import { fieldLabel, fieldName, fieldProps } from "./fieldName";

export const createItem2 = (attr: ZAttribute, ctx: BuilderCtx): FormBlockItem =>
  createItemOpt(attr, ctx) ??
  readOnlyInput(fieldName(attr), fieldLabel(attr), {}, {});

export const getComponentEditor = (
  editorInfo: ZAttrEditInfo | undefined,
  typesMap: Record<number, string>,
  attr: ZAttribute,
) => {
  let component = editorInfo?.component;
  if (!component) {
    const typeName = typesMap[attr.valueType];
    const list = editorsByType[typeName as AttrTypeName];
    const editor = list?.[0];
    if (editor) {
      try {
        component = zAttrComponentEditor.parse({ editor });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e); // для отладки
      }
    }
  }
  return component;
};

const createItemOpt = (
  attr: ZAttribute,
  ctx: BuilderCtx,
): FormBlockItem | undefined => {
  const { name, label } = fieldProps(attr);
  const editorInfo = getEditorInfo(attr.viewStyles);
  const viewInfo = getViewInfo(attr.viewType);
  const canUpdate =
    canUpdateAttr(ctx, attr) && hasPermissionIn(attr, Permission.attrUpdate);
  const restrictValues = ctx.restrictValues ?? true;
  const disabled = !canUpdate;
  const newEntVirtualPlaceholder =
    !!attr.systemAttributeType && !ctx.entityId
      ? t("Data will be displayed after saving")
      : undefined;

  // Дефолтные компоненты должны уметь себя рисовать без какой-либо дополнительной информации
  // То есть, у них не должно быть required-полей.

  const component = getComponentEditor(editorInfo, ctx.typesMap, attr);
  const srcItemProps = editorInfo?.itemProps ?? {};
  const dstItemProps = cvtItemProps(
    srcItemProps,
    ctx.typesMap[attr.valueType] as AttrTypeName,
    component?.editor,
  );

  if (!component) return undefined;
  // Не удалось приспособить словарь. Похоже что switch - единственный способ в данном случае
  // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#discriminated-unions

  switch (component.editor) {
    case "Checkbox":
      return blockItem(
        name,
        " ",
        EntityCheckbock,
        { label, disabled },
        { ...dstItemProps, initialValue: ["false"] },
      );
    case "ContentLanguage":
      return blockItem(
        name,
        label,
        EntityContentLanguage,
        { disabled },
        dstItemProps,
      );
    case "Switch":
      return blockItem(
        name,
        label,
        EntitySwitch,
        { disabled },
        { ...dstItemProps, initialValue: ["false"] },
      );
    case "Select": {
      const isMulti = ctx.typesMap[attr.valueType] === AttrTypeName.dictMulti;
      return buildReferenceItem(
        attr,
        isMulti,
        dstItemProps,
        {
          ...makeSelectProps(component, isMulti, restrictValues),
          disabled,
        },
        ctx.registerRestriction,
      );
    }
    case "DatePicker":
      return blockItem(
        name,
        label,
        EntityDatePicker,
        {
          showTime: ctx.typesMap[attr.valueType] === AttrTypeName.dateTime,
          disabled,
          placeholder: newEntVirtualPlaceholder,
        },
        dstItemProps,
      );

    case "InputNumber": {
      const compProps = makeInputNumberProps(component);
      if (ctx.typesMap[attr.valueType] === AttrTypeName.int)
        compProps.precision = 0;
      return buildNumber(name, label, dstItemProps, {
        ...compProps,
        disabled,
      });
    }

    case "Input":
      return blockItem(
        name,
        label,
        EntityInput,
        {
          allowClear: true,
          ...makeInputProps(component),
          disabled,
          placeholder: newEntVirtualPlaceholder,
        },
        dstItemProps,
      );

    case "Link":
      return blockItem(
        name,
        label,
        EntityLinkEdit,
        {
          allowClear: true,
          disabled,
        },
        {
          ...dstItemProps,
          rules: [
            {
              pattern: /^https?:\/\//,
              message: t("The link must start with"),
            },
          ],
        },
      );

    case "MaskedInput":
      return blockItem(
        name,
        label,
        EntityMaskedInput,
        {
          ...makeMaskedInputProps(component),
          disabled,
        },
        dstItemProps,
      );

    case "TextArea":
      return blockItem(
        name,
        label,
        EntityTextArea,
        { ...makeTextAreaProps(component), disabled },
        dstItemProps,
      );

    case "ObectRefSelector":
      return blockItem(
        name,
        label,
        ObjectRefSelector,
        {
          ...makeObjectRefSelectorProps(
            attr,
            component,
            ctx.labelsData,
            ctx.objectName,
          ),
          disabled,
          label,
          entityId: ctx.entityId,
        },
        dstItemProps,
      );
    case "ObjectRefTable":
      return blockItem(
        name,
        label,
        ObjectRefTable,
        {
          ...makeObjectRefTableProps(attr, component, viewInfo),
          disabled,
          label,
          entityId: ctx.entityId,
        },
        dstItemProps,
      );
    case "TimePicker":
      return blockItem(
        name,
        label,
        EntityTimePicker,
        { disabled },
        dstItemProps,
      );
    case "Uploader":
      return buildUploader(
        attr,
        ctx.typesMap[attr.valueType] || "",
        dstItemProps,
        makeUploaderProps(component),
        disabled,
      );
    case "ImageCarousel":
      return buildImageCarousel(
        attr,
        dstItemProps,
        makeImageCarouselProps(component),
        disabled,
      );
    case "PersonSelect":
      return blockItem(
        name,
        label,
        PersonSelect,
        {
          objectId: ctx.objectId,
          allowClear: true,
          ...makeAttrPersonSelectProps(component),
          attribute: attr,
          disabled,
        },
        dstItemProps,
      );
    case "ChildEntities":
      return blockItem(
        name,
        label,
        ChildEntities,
        {
          ...makeChildEntitiesProps(attr, component),
          disabled,
          entityId: ctx.entityId,
        },
        dstItemProps,
      );
    case "Formula": {
      return buildNumber(name, label, dstItemProps, {
        ...makeFormulaProps(component),
        disabled: true,
      });
    }
    case "FormulaWithFinancial": {
      return blockItem(
        name,
        label,
        FinancialInput,
        makeFormulaWithFinancialProps(attr, component),
        dstItemProps,
      );
    }
    case "LinkedValue": {
      return blockItem(
        name,
        label,
        AttrLinked,
        makeAttrLinkedProps(attr, ctx),
        dstItemProps,
      );
    }
    case "FinancialInput": {
      return blockItem(
        name,
        label,
        FinancialInput,
        { ...makeFinancialInputProps(attr), disabled },
        dstItemProps,
      );
    }
    case "ColorCard": {
      return blockItem(
        name,
        label,
        ColorCard,
        makeAttrColorCardProps(component),
        dstItemProps,
      );
    }
    case "Article":
      return readOnlyInput(name, label, dstItemProps, {
        placeholder: newEntVirtualPlaceholder,
      });
    default:
      return undefined;
  }
};

export const buildNumber = (
  name: string,
  label: string,
  itemProps: ItemProps,
  compProps?: PropsEntityInputNumber,
) =>
  blockItem(
    name,
    label,
    EntityInputNumber,
    { style: { width: "100%" }, ...compProps },
    itemProps,
  );
